<script setup lang="ts">
import api from "@/api";
import { baseURL } from "@/api/config";
import { DateInstance, utcToLocalDateTime } from "@/assets/js/dateHelper";
import type { TDriver, TRide, TRideStatusKey } from "@/assets/js/globalTypes";
import { getApiErrorMessage, rideStatusText } from "@/assets/js/helpers";
import { openConfirmModal } from "@/components/modals/modalDefinitions";
import { $t } from "@/i18n";
import { useRoute } from "vue-router";
import AddSubModal from "./AddSubModal.vue";
import EditTaxiModal from "./EditTaxiModal.vue";
import { useToastStore } from "@/store/toast";
import driverAltIcon from "@/assets/image/default-driver.png";
import carAltIcon from "@/assets/image/default-car.png";
import dayjs from "dayjs";

definePage({
	name: "SingleTaxiPage",
	meta: {
		requiresLogin: true,
	},
});

const route = useRoute();
const tax_id = computed(() => Number(route.params.id) || 0);

const driver = ref<TDriver | null>(null);
const rides = ref<TRide[]>();
const current_ride = ref<TRide>();
const statistics = ref<{ [k: string]: any }>();

const showAddSubscription = ref(false);
const showEditTaxi = ref(false);
const isSavingTaxi = ref(false);
const editEmailError = ref("");
const toastStore = useToastStore();
watch(
	tax_id,
	() => {
		getDriverInfo();
		getDriverStatistics();
	},
	{ immediate: true },
);

async function getDriverInfo() {
	try {
		const res = await api.getDriverInfo({ tax_id: tax_id.value });

		console.log("CEO RESPONSE:", res.data);
		console.log("DRIVER IZ RESPONSE:", res.data.data.driver);
		driver.value = res.data.data.driver;
		console.log("DRIVER POSLE DODELE:", driver.value);
		current_ride.value = res.data.data.current_ride;

		rides.value = res.data.data.rides;
	} catch (error) {
		console.log(error);
	}
}

async function getDriverStatistics() {
	try {
		const res = await api.getDriverStatistics(tax_id.value);
		statistics.value = res.data.data;
	} catch (error) {
		console.log(error);
	}
}

async function verifyDriver() {
	try {
		await api.editDriver({
			tax_id: tax_id.value,
			tax_privilege: 10,
		});
		getDriverInfo();
	} catch (error) {
		console.log(error);
	}
}

function getRideStatus(ride_status: TRideStatusKey) {
	return rideStatusText(ride_status);
}

async function banDriver() {
	try {
		await api.banDriver(tax_id.value);
		await getDriverInfo();
	} catch (error) {
		console.log(error);
	}
}

function handleBanClick() {
	if (!driver.value) return;
	const name = driver?.value.tax_name + " " + driver?.value.tax_lastname;
	openConfirmModal(
		{
			name,
		},
		() => {
			banDriver();
		},
		$t("app.actions.ban-confirm", { name }),
	);
}

function handleUnnanClick() {
	if (!driver.value) return;
	const name = driver?.value.tax_name + " " + driver?.value.tax_lastname;
	openConfirmModal(
		{
			name,
		},
		() => {
			verifyDriver();
		},
		$t("app.actions.unban-confirm", { name }),
	);
}

async function downloadFile(file_id: number | null | undefined) {
	if (!file_id) {
		throw new Error("invalid-file-id");
	}
	try {
		let res = await api.downloadDocument(file_id);
		const blob = res.data;

		let headerLine = res.headers["content-disposition"];
		let startFileNameIndex = headerLine.indexOf('"') + 1;
		let endFileNameIndex = headerLine.lastIndexOf('"');
		let filename = headerLine.slice(startFileNameIndex, endFileNameIndex);

		const fake_link = document.createElement("a");
		const url = window.URL.createObjectURL(blob);
		fake_link.href = url;
		fake_link.target = "_blank";
		fake_link.download = filename;
		document.body.append(fake_link);
		fake_link.click();
		fake_link.remove();
		window.URL.revokeObjectURL(url);
	} catch (e) {
		console.log(e);
	}
}
async function eraseSubscription(sub_id?: number | null) {
	if (!sub_id) return;

	try {
		await api.deleteSubscription(sub_id);
		await getDriverInfo();
	} catch (error) {
		console.log(error);
	}
}
function handleDeleteSubscriptionClick() {
	const sub_id = driver.value?.sub_active_id;
	if (!sub_id) return;

	openConfirmModal(
		{
			name: $t("app.driver.subscription"),
			data: sub_id,
		},
		(sub_id: number) => {
			eraseSubscription(sub_id);
		},
		$t("app.actions.delete-sub"),
	);
}
async function handleAddSubscription(payload: {
	sub_start_time: string;
	sub_end_time: string;
}) {
	try {
		await api.addSubscription({
			tax_id: tax_id.value,
			...payload,
		});

		showAddSubscription.value = false;

		await getDriverInfo();
	} catch (error) {
		console.log(error);
	}
}
function closeEditTaxi() {
	showEditTaxi.value = false;
	editEmailError.value = "";
}
async function handleEditTaxi(formData: FormData) {
	isSavingTaxi.value = true;
	editEmailError.value = "";
	try {
		await api.editDriverDetails(tax_id.value, formData);
		showEditTaxi.value = false;
		toastStore.openToastSuccess($t("app.driver.edit-success"));
		await getDriverInfo();
	} catch (error) {
		console.log(error);
		if (getApiErrorMessage(error) === "email-exists") {
			editEmailError.value = $t("app.driver.email-exists");
			toastStore.openToastError(editEmailError.value);
			return;
		}
		toastStore.openToastError($t("app.driver.edit-error"));
	} finally {
		isSavingTaxi.value = false;
	}
}
async function extendSubscription() {
	const now = dayjs().subtract(121, "minute");

	const start = driver.value?.sub_start_time
		? dayjs(driver.value.sub_start_time)
		: now;

	const end = driver.value?.sub_end_time
		? dayjs(driver.value.sub_end_time).add(30, "day")
		: now.add(30, "day");

	const payload = {
		tax_id: tax_id.value,
		sub_start_time: start.format("YYYY-MM-DD HH:mm:ss"),
		sub_end_time: end.format("YYYY-MM-DD HH:mm:ss"),
	};

	try {
		await api.addSubscription(payload);
		await getDriverInfo();
	} catch (error) {
		console.log(error);
	}
}
</script>
<template>
	<div id="taxi-page-single">
		<div class="header-wrap">
			<div class="avatar">
				<div
					v-if="driver"
					class="img-wrap"
				>
					<img
						:src="driver?.image ? baseURL + driver.image : driverAltIcon"
						:alt="
							`${driver?.tax_name || ''} ${driver?.tax_lastname || ''}`.trim() ||
							'Vozač'
						"
					/>
				</div>
				<h2>{{ driver?.tax_name }} {{ driver?.tax_lastname }}</h2>
				<p>
					<strong>
						{{
							driver?.tax_privilege
								? $t("app.driver.verified")
								: $t("app.driver.unverified")
						}}
					</strong>
				</p>
			</div>
			<div class="avatar">
				<div
					v-if="driver"
					class="img-wrap"
				>
					<img
						:src="
							driver?.tax_car_image
								? baseURL + driver?.tax_car_image
								: carAltIcon
						"
						alt=""
					/>
				</div>
				<h2>{{ driver?.tax_model }}</h2>
				<p>
					{{ $t("app.driver.plates") }}:
					<strong>{{ driver?.tax_plates }}</strong>
				</p>
			</div>
		</div>
		<div class="driver-info">
			<section>
				<p>
					{{ $t("app.driver.status") }}:
					<strong>
						{{
							driver?.tax_active
								? $t("app.driver.active")
								: $t("app.driver.inactive")
						}}
					</strong>
				</p>
				<p>
					{{ $t("app.auth.email") }}:
					<strong>{{ driver?.tax_email }}</strong>
				</p>
				<p>
					{{ $t("app.driver.assotiation") }}:
					<strong>{{ driver?.aso_name }}</strong>
				</p>
				<p>
					{{ $t("app.driver.phone") }}:
					<strong>{{ driver?.tax_phone }}</strong>
				</p>
				<p>
					{{ $t("app.driver.subscription-start") }}:
					<strong>{{ utcToLocalDateTime(driver?.sub_start_time) }}</strong>
				</p>

				<p>
					{{ $t("app.driver.subscription-end") }}:
					<strong>{{ utcToLocalDateTime(driver?.sub_end_time) }}</strong>
				</p>
				<p>
					{{ $t("app.driver.subscription") }}:
					<strong>
						{{
							driver?.sub_is_active
								? $t("app.driver.subscription-active")
								: $t("app.driver.subscription-inactive")
						}}
					</strong>
				</p>
			</section>
			<section>
				<router-link
					v-if="current_ride"
					:to="{ name: 'SingleRidePage', params: { id: current_ride.rid_id } }"
				>
					🟢 {{ $t("app.ride-actions.in-ride") }}
				</router-link>
				<span v-else>🔴 {{ $t("app.ride-actions.not-in-ride") }}</span>
			</section>

			<section>
				<p v-if="rides && rides[0]">
					{{ $t("app.driver.last-ride-date") }}:
					<strong>
						{{
							DateInstance.getLocalTimeFromISO(
								rides[0].rid_request_time,
								"HH:mm DD.MM.YYYY",
							)
						}}
					</strong>
				</p>
				<p>
					{{
						rides && rides.length > 0
							? $t("app.home.last-five-rides") + ":"
							: $t("app.ride-actions.no-rides")
					}}
				</p>
				<div
					v-for="ride in rides"
					:key="ride.rid_id"
				>
					<router-link
						:to="{ name: 'SingleRidePage', params: { id: ride.rid_id } }"
					>
						{{ $t("app.ride-actions.ride-id") }}: {{ ride.rid_id }}
					</router-link>
					<span>&nbsp;- {{ getRideStatus(ride.rid_status) }}</span>
				</div>
			</section>
			<section
				v-if="driver"
				class="edit-section"
			>
				<button
					class="alt"
					@click="showEditTaxi = true"
				>
					{{ $t("app.actions.edit") }}
				</button>
			</section>
			<button
				v-if="!driver?.tax_privilege"
				@click="verifyDriver"
			>
				{{ $t("app.actions.verify") }}
			</button>
			<section
				v-if="driver?.tax_privilege && driver?.tax_privilege !== 1"
				style="display: flex; gap: 10px"
			>
				<button
					v-if="driver?.sub_start_time"
					class="btn-red"
					@click="handleDeleteSubscriptionClick"
				>
					{{ $t("app.actions.delete") }}
				</button>
				<button @click="showAddSubscription = true">
					{{ $t("app.actions.add-sub") }}
				</button>
				<button
					class="alt"
					@click="extendSubscription"
				>
					{{ $t("app.actions.extend-sub") }}
				</button>
			</section>
			<button
				v-if="driver?.tax_privilege === 1"
				class="btn-red"
				@click="handleUnnanClick"
			>
				{{ $t("app.actions.unban") }}
			</button>
			<button
				v-if="driver?.tax_privilege !== 1"
				class="btn-red"
				@click="handleBanClick"
			>
				{{ $t("app.actions.ban") }}
			</button>
		</div>
		<div class="licence-wrap">
			<h2>{{ $t("app.driver.licence-and-registration") }}</h2>
			<div class="licence-content">
				<button @click="downloadFile(driver?.tax_licence)">
					{{ $t("app.taxi.download-licence") }}
				</button>
				<button @click="downloadFile(driver?.tax_registration)">
					{{ $t("app.taxi.download-registration") }}
				</button>
			</div>
		</div>
		<div class="statistic-wrap">
			<h2>{{ $t("app.taxi.rides-last-30-days") }}:</h2>
			<p>
				{{ $t("app.labels.total") }}:
				<strong>{{ statistics?.total || 0 }}</strong>
			</p>
			<p>
				{{ $t("app.ride-status.finished") }}:
				<strong>{{ statistics?.finished_rides || 0 }}</strong>
			</p>
			<p>
				{{ $t("app.ride-status.canceled") }}:
				<strong>{{ statistics?.canceled_user || 0 }}</strong>
			</p>
			<p>
				{{ $t("app.ride-status.canceled-driver") }}:
				<strong>{{ statistics?.canceled_driver || 0 }}</strong>
			</p>
		</div>
	</div>
	<add-sub-modal
		v-if="showAddSubscription"
		@cancel="showAddSubscription = false"
		@save="handleAddSubscription"
	/>
	<edit-taxi-modal
		v-if="showEditTaxi && driver"
		:driver="driver"
		:loading="isSavingTaxi"
		:email-error="editEmailError"
		@cancel="closeEditTaxi"
		@save="handleEditTaxi"
		@clear-email-error="editEmailError = ''"
	/>
</template>
<style lang="scss" scoped>
.parent {
	display: grid;
}

#taxi-page-single {
	padding: 30px;
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	grid-template-rows: repeat(2, auto);
	grid-column-gap: 10px;
	grid-row-gap: 25px;
	grid-template-areas:
		"header-wrap driver-info"
		"licence-wrap statistic-wrap";
	.credits-header {
		display: flex;
		gap: 5px;
		align-items: center;
		.input-wrap {
			display: flex;
			align-items: center;
		}
	}
	.header-wrap {
		grid-area: header-wrap;
		display: flex;
		flex-wrap: wrap;
		gap: 30px;
		.avatar {
			max-width: max(300px, 20vw);
			display: flex;
			flex-direction: column;
			.img-wrap {
				flex: 1 1;
			}
			img {
				width: 300px;
				height: 400px;
				object-fit: contain;
				object-position: center;
			}
		}
	}
	.driver-info {
		grid-area: driver-info;
		section {
			margin-bottom: 10px;
		}
		.edit-section {
			margin: 20px 0;
		}
		a {
			color: $app-accent-color1;
			font-weight: 600;
			font-size: 1.02rem;
		}
	}
	.licence-wrap {
		grid-area: licence-wrap;
		.licence-content {
			margin-top: 20px;
			display: flex;
			flex-wrap: wrap;
			gap: 20px;
		}
	}
	.btn-red {
		background-color: $btn-red;
	}

	h2 {
		font-weight: 600;
		font-size: 1.3rem;
	}
	.statistic-wrap {
		grid-area: statistic-wrap;
	}

	@media screen and (width <= 768px) {
		grid-template-columns: 1fr;
		grid-template-rows: repeat(4, auto);
		grid-template-areas:
			"header-wrap"
			"driver-info"
			"licence-wrap"
			"statistic-wrap";
	}
}
</style>
